// Restyles the RE-ZIP app embed's cart widget with the theme's typography and checkbox, so its
// heading, description and checkbox sit in the cart footer like the theme's own text and the
// terms checkbox (theme.css .bold.text-base, .text-subdued.text-sm, .checkbox / .checkbox ~ label).
//
// RE-ZIP renders its widget inside an open shadow root with no `part` attributes, so no theme
// stylesheet can reach it. Instead one constructed stylesheet is adopted into each widget's
// shadow root, and a second, page-level one sets the spacing between the widget and the terms
// line. The rules key on RE-ZIP's internal class names as of re-zip-surisuri-25 — if an
// app update renames them, the rules stop matching and the widget shows the app's own design
// again; nothing breaks. While this runs, the embed's own colour and font-size settings no
// longer apply; its logo setting still does.
//
// It also appends the theme's "Read more" link (Theme settings > Cart > RE-ZIP) to the end of
// the widget's description, when the section passes one. The restyle and the link are
// independent: the script tag's data-theme-style and data-read-more-* attributes turn each on.
//
// Both cart sections load this file (the drawer also exists on /cart), hence the guard.
if (!window.dtailsRezipThemeStyle) {
  window.dtailsRezipThemeStyle = true;

  const { dataset } = document.currentScript;
  const themeStyle = 'themeStyle' in dataset && 'replaceSync' in CSSStyleSheet.prototype;
  const { readMoreText, readMoreUrl } = dataset;
  const readMore = Boolean(readMoreText && readMoreUrl);

  // Constructed stylesheets adopted into every widget's shadow root
  const shadowSheets = [];

  // window.__rezip is written by the embed itself, so no config means the embed is off.
  // Browsers without constructable stylesheets keep the app's own design.
  if (window.__rezip && themeStyle) {
    const sheet = new CSSStyleSheet();

    sheet.replaceSync(`
      /* No card: the widget sits flush in the cart footer like its neighbours */
      .rezip-widget {
        background: none;
        border: 0;
        border-radius: 0;
        box-shadow: none;
        margin: 0;
        padding: 0;
        gap: var(--spacing-3);
        font-family: inherit;
        color: inherit;
      }

      /* Mirrors the cart line item title (.bold.text-base), so it stays below the total's .h5 */
      .rezip-title {
        margin: 0;
        color: inherit;
        font-weight: bold;
        font-size: var(--text-base);
        line-height: 1.6;
      }

      /* Mirrors .text-subdued.text-sm */
      .rezip-description {
        margin: 0 0 var(--spacing-1);
        color: rgb(var(--text-color) / .7);
        font-size: var(--text-sm);
        line-height: 1.6;
      }

      /* Mirrors .checkbox-container */
      .rezip-toggle {
        align-items: baseline;
        gap: 0;
      }

      /* Mirrors .checkbox, plus the UA margin reset */
      .rezip-toggle input[type=checkbox] {
        -webkit-appearance: none;
        appearance: none;
        width: var(--spacing-4);
        height: var(--spacing-4);
        margin: 0;
        background: rgb(var(--text-color) / .15) var(--checkmark-svg-url) no-repeat center;
        background-size: 0;
        border-radius: 2px;
        flex-shrink: 0;
        transition: background-color .2s ease-in-out;
        position: relative;
        top: .1875rem;
        box-shadow: inset 1px 1px 2px #0000001a;
      }

      .rezip-toggle input[type=checkbox]:checked {
        background-color: rgb(var(--accent));
        background-size: 10px 8px;
      }

      /* Mirrors .checkbox ~ label.text-sm */
      .rezip-toggle-label {
        color: inherit;
        font-size: var(--text-sm);
        line-height: 1.6;
        opacity: .7;
        padding-inline-start: var(--spacing-3);
      }

      .rezip-toggle input[type=checkbox]:checked + .rezip-toggle-label {
        opacity: 1;
        transition: all .2s ease-in-out;
      }
    `);

    // Page-level spacing: the cart footers space their rows 16–24px apart, but the widget and the
    // terms checkbox read as one group, so they sit 12px apart — in either order, as the embed's
    // "Place the widget" setting can put the widget above or below the terms. Only containers the
    // embed injected ([data-rezip-injected]); an app block keeps the page's own spacing.
    const pageSheet = new CSSStyleSheet();

    pageSheet.replaceSync(`
      /* Cart drawer footer (.v-stack.gap-4): 16px → 12px */
      [data-rezip-injected] + cart-terms,
      cart-terms + [data-rezip-injected] {
        margin-block-start: 20px;
      }

      /* Cart page (.cart-form, gap: var(--spacing-5)): 20px → 12px */
      .cart-form :is([data-rezip-injected] + cart-terms, cart-terms + [data-rezip-injected]) {
        margin-block-start: 20px;
      }

      /* Both at sm:gap-6 / var(--spacing-6): 24px → 12px */
      @media screen and (min-width: 700px) {
        [data-rezip-injected] + cart-terms,
        cart-terms + [data-rezip-injected],
        .cart-form :is([data-rezip-injected] + cart-terms, cart-terms + [data-rezip-injected]) {
          margin-block-start: 20px;
        }
      }
    `);

    document.adoptedStyleSheets = [...document.adoptedStyleSheets, pageSheet];
    shadowSheets.push(sheet);
  }

  if (window.__rezip && readMore && 'replaceSync' in CSSStyleSheet.prototype) {
    const linkSheet = new CSSStyleSheet();

    // Mirrors .link, in the description's own colour
    linkSheet.replaceSync(`
      .rezip-read-more {
        color: inherit;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    `);

    shadowSheets.push(linkSheet);
  }

  if (window.__rezip && (shadowSheets.length || readMore)) {
    // Appends the link once; a no-op until the app has rendered the description
    const addReadMore = (root) => {
      const description = root.querySelector('.rezip-description');

      if (!description || description.querySelector('.rezip-read-more')) {
        return;
      }

      const link = document.createElement('a');
      link.className = 'rezip-read-more';
      link.href = readMoreUrl;
      link.textContent = readMoreText;
      description.append(' ', link);
    };

    // Shadow roots already watched for the read more link
    const watched = new WeakSet();

    // Only the embed-placed widgets (drawer-auto / drawer-custom, which the embed also uses on
    // /cart). A RE-ZIP app block (placement="block") keeps the app's own design.
    const adopt = () => {
      for (const widget of document.querySelectorAll('rezip-widget[placement^="drawer"]')) {
        const root = widget.shadowRoot;

        if (!root) {
          continue;
        }

        // Assign rather than push, which older Safari versions do not support
        const missing = shadowSheets.filter((sheet) => !root.adoptedStyleSheets.includes(sheet));

        if (missing.length) {
          root.adoptedStyleSheets = [...root.adoptedStyleSheets, ...missing];
        }

        // The page-level observer below cannot see inside the shadow root, where the app renders
        // and re-renders the description, so each root gets its own
        if (readMore && !watched.has(root)) {
          watched.add(root);
          addReadMore(root);
          new MutationObserver(() => addReadMore(root)).observe(root, { childList: true, subtree: true });
        }
      }
    };

    adopt();

    // RE-ZIP re-creates the widget, with a new shadow root, whenever the cart drawer re-renders.
    // Observer callbacks run before the next paint, so the app's own design never flashes.
    new MutationObserver(adopt).observe(document.body, { childList: true, subtree: true });
  }
}
